
import React, { useState } from 'react';
import { User } from '../types.ts';
import { UserCircle, Save, Phone, Mail, Shield, CheckCircle2, Image as ImageIcon } from 'lucide-react';

interface ProfileViewProps {
  user: User;
  onUpdate: (updatedUser: Partial<User>) => void;
}

const ProfileView: React.FC<ProfileViewProps> = ({ user, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email || '',
    whatsapp: user.whatsapp || '',
    avatar_url: user.avatar_url || '',
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const formatWhatsApp = (value: string) => {
    const digits = value.replace(/\D/g, '');
    let masked = digits;
    if (digits.length > 0) {
      masked = '(' + digits.substring(0, 2);
      if (digits.length > 2) {
        masked += ') ' + digits.substring(2, 7);
      }
      if (digits.length > 7) {
        masked += '-' + digits.substring(7, 11);
      }
    }
    return masked;
  };

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatWhatsApp(e.target.value);
    setFormData({ ...formData, whatsapp: masked });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate({
      name: formData.name,
      whatsapp: formData.whatsapp,
      avatar_url: formData.avatar_url,
      email: formData.email
    });
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold uppercase tracking-tight">Meu Perfil</h2>
          <p className="text-[#848e9c] text-sm">Gerencie suas informações de conta e contato.</p>
        </div>
        <div className={`flex items-center gap-2 text-[#00ff66] bg-[#00ff66]/10 px-4 py-2 rounded-xl transition-all duration-300 ${showSuccess ? 'opacity-100' : 'opacity-0'}`}>
          <CheckCircle2 size={16} />
          <span className="text-xs font-bold uppercase tracking-wider">Perfil Atualizado!</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-6">
          <div className="bg-[#1e2329] p-8 rounded-3xl border border-[#474d57] flex flex-col items-center text-center shadow-xl">
            <div className="w-24 h-24 rounded-full bg-[#2b3139] border-2 border-[#00ff66] overflow-hidden flex items-center justify-center text-[#00ff66] mb-4 shadow-lg shadow-green-500/10">
              {formData.avatar_url ? (
                <img src={formData.avatar_url} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <UserCircle size={48} />
              )}
            </div>
            <h3 className="text-lg font-bold">{formData.name}</h3>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#848e9c] mb-4">{user.role}</span>
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase ${user.active ? 'bg-[#00ff66]/10 text-[#00ff66]' : 'bg-[#f6465d]/10 text-[#f6465d]'}`}>
              {user.active ? 'Ativo' : 'Inativo'}
            </div>
          </div>

          <div className="bg-[#1e2329] p-6 rounded-3xl border border-[#474d57] shadow-lg">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-[#848e9c] mb-4 flex items-center gap-2">
              <Shield size={14} className="text-[#00ff66]" />
              Segurança
            </h4>
            <p className="text-xs text-[#848e9c] leading-relaxed">
              Sua conta está protegida por criptografia de ponta. Mantenha seus dados de contato atualizados para garantir a integridade da sua conta.
            </p>
          </div>
        </div>

        <div className="md:col-span-2">
          <form onSubmit={handleSubmit} className="bg-[#1e2329] p-8 rounded-3xl border border-[#474d57] shadow-xl space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#848e9c] block">Nome Completo</label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#848e9c] group-focus-within:text-[#00ff66] transition-colors">
                    <UserCircle size={18} />
                  </div>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:border-[#00ff66] outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#848e9c] block">URL da Foto (Avatar)</label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#848e9c] group-focus-within:text-[#00ff66] transition-colors">
                    <ImageIcon size={18} />
                  </div>
                  <input
                    type="text"
                    value={formData.avatar_url}
                    onChange={e => setFormData({ ...formData, avatar_url: e.target.value })}
                    placeholder="https://exemplo.com/sua-foto.jpg"
                    className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:border-[#00ff66] outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#848e9c] block">E-mail Operacional</label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#848e9c] group-focus-within:text-[#00ff66] transition-colors">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="exemplo@fifalytics.pro"
                    className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:border-[#00ff66] outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#848e9c] block">WhatsApp (Notificações)</label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#848e9c] group-focus-within:text-[#00ff66] transition-colors">
                    <Phone size={18} />
                  </div>
                  <input
                    type="text"
                    value={formData.whatsapp}
                    onChange={handleWhatsAppChange}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                    className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:border-[#00ff66] outline-none transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[#474d57]/30">
              <button
                type="submit"
                className="w-full bg-[#00ff66] text-[#0b0e11] font-black py-5 rounded-2xl text-sm flex items-center justify-center gap-2 hover:shadow-[0_4px_25px_rgba(0,255,102,0.3)] transition-all transform active:scale-[0.98] uppercase tracking-widest"
              >
                <Save size={18} />
                Salvar Alterações
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
